import React, { useEffect, useState } from 'react';
import { ActivityEditorProps } from './types';
import ActivityToolbar from './ActivityToolbar';
import { 
  LessonEditor, 
  QuizEditor, 
  AssignmentEditor, 
  ExerciseEditor, 
  DefaultEditor 
} from './editors';
import AiChatDialog from '../../../ai-agent/AiChatDialog';
import { getPreviewRenderer } from '../../../ai-agent/previewRenderers';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import toast from 'react-hot-toast';

// Helper function to get the appropriate chain name for each activity type
const getChainNameForActivityType = (activityType: string): string => {
  switch (activityType) {
    case 'quiz':
      return 'quiz_generator'; // Use quiz_generator for quiz content
    case 'lesson':
      return 'lesson_generator'; // Use lesson_generator for lesson content
    case 'assignment':
      return 'assignment_generator'; // Use educational_assistant for assignments
    case 'exercise':
      return 'exercise_generator'; // Use course_generator for exercises
    default:
      return ''; // Default fallback
  }
};

// Helper function to get quick prompts based on activity type
const getQuickPromptsForActivityType = (activityType: string) => {
  const basePrompts = [
    { id: '1', text: 'Tạo nội dung cơ bản', icon: '📝' },
    { id: '2', text: 'Thêm ví dụ thực tế', icon: '💡' },
    { id: '3', text: 'Tạo nội dung nâng cao', icon: '🚀' }
  ];

  switch (activityType) {
    case 'lesson':
      return [
        ...basePrompts,
        { id: '4', text: 'Tạo bài giảng với slide', icon: '📊' },
        { id: '5', text: 'Thêm video hướng dẫn', icon: '🎥' },
        { id: '6', text: 'Tạo tóm tắt bài học', icon: '📋' },
        { id: '7', text: 'Thêm câu hỏi thảo luận', icon: '💬' }
      ];
    case 'quiz':
      return [
        ...basePrompts,
        { id: '4', text: 'Tạo câu hỏi trắc nghiệm', icon: '❓' },
        { id: '5', text: 'Tạo câu hỏi đúng/sai', icon: '✅' },
        { id: '6', text: 'Tạo câu hỏi điền khuyết', icon: '🔤' },
        { id: '7', text: 'Tạo câu hỏi tự luận', icon: '✍️' },
        { id: '8', text: 'Tạo câu hỏi khó', icon: '🧠' }
      ];
    case 'assignment':
      return [
        ...basePrompts,
        { id: '4', text: 'Tạo bài tập nhóm', icon: '👥' },
        { id: '5', text: 'Tạo bài tập cá nhân', icon: '👤' },
        { id: '6', text: 'Tạo hướng dẫn chi tiết', icon: '📖' },
        { id: '7', text: 'Tạo tiêu chí đánh giá', icon: '📊' }
      ];
    case 'exercise':
      return [
        ...basePrompts,
        { id: '4', text: 'Tạo bài tập thực hành', icon: '🔧' },
        { id: '5', text: 'Tạo bài tập luyện tập', icon: '🏋️' },
        { id: '6', text: 'Tạo bài tập tương tác', icon: '🔄' },
        { id: '7', text: 'Tạo bài tập sáng tạo', icon: '🎨' }
      ];
    default:
      return basePrompts;
  }
};

// Main ActivityEditor Component
const ActivityEditor: React.FC<ActivityEditorProps> = ({ 
  activity, 
  onActivityUpdate,
  moduleStructure,
  sectionId 
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<any>(activity);


  useEffect(() => {
    setEditingActivity(activity);
  }, [activity]);

  // Handle save action
  const handleSave = async () => {
    if (!onActivityUpdate) return;
    
    try {
      setIsSaving(true);
      // The actual saving will be handled by the specific editor components
      // This is just a placeholder for the toolbar button
      await onActivityUpdate(editingActivity);
    } catch (error) {
      console.error('Error saving activity:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle preview action
  const handlePreview = () => {
    // Implement preview functionality
    toast(`Preview for ${activity.title} would open here`);
  };

  // Handle delete action — mở Dialog, chỉ thực hiện khi bấm "Xác nhận".
  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    setShowDeleteConfirm(false);
    // Implement delete functionality
    toast('Delete functionality would be implemented here');
  };

  const handleActivityChange = (updatedActivity: any) => {
    setEditingActivity(updatedActivity);
  };
  
  // Handle AI assist action
  const handleAiAssist = () => {
    setShowAiDialog(true);
  };
  
  // Handle AI generated content
  const handleAiGeneratedContent = (content: any) => {
    console.log('AI generated content in ActivityEditor:', content);
    
    // Dispatch custom event to notify the specific editor
    const event = new CustomEvent('ai-content-generated', {
      detail: {
        activityId: activity.activity_id,
        content: content
      }
    });
    window.dispatchEvent(event);
  };

  // Render the appropriate editor based on activity type
  const renderEditor = () => {
    switch (activity.type) {
      case 'lesson':
        return <LessonEditor activity={activity} onActivityUpdate={onActivityUpdate} />;
      case 'quiz':
        return <QuizEditor activity={activity} onActivityUpdate={onActivityUpdate} onActivityChange={handleActivityChange} />;
      case 'assignment':
        return <AssignmentEditor activity={activity} onActivityUpdate={onActivityUpdate} />;
      case 'exercise':
        return <ExerciseEditor activity={activity} onActivityUpdate={onActivityUpdate} />;
      default:
        return <DefaultEditor activity={activity} onActivityUpdate={onActivityUpdate} />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <ActivityToolbar 
        activity={activity}
        onSave={handleSave}
        onPreview={handlePreview}
        onDelete={handleDelete}
        onAiAssist={handleAiAssist}
        isSaving={isSaving}
      />
      <div className="p-6">
        {renderEditor()}
      </div>
      
      {/* AI Chat Dialog */}
      <AiChatDialog
        isOpen={showAiDialog}
        onClose={() => setShowAiDialog(false)}
        title={activity.title}
        welcomeMessage="Bắt đầu cuộc trò chuyện với trợ lý AI"
        welcomeSubMessage={`Mô tả nội dung bạn muốn tạo cho ${activity.type} này`}
        inputPlaceholder={`Mô tả nội dung bạn muốn tạo cho ${activity.type} này...`}
        renderPreview={getPreviewRenderer(activity.type)}
        contextData={{
          currentActivity: {
            type: activity.type,
            title: activity.title
          },
          moduleStructure: {
            sections: moduleStructure.sections?.map((section: any) => ({ name: section.name }))
          }
        }}
        chainName={getChainNameForActivityType(activity.type)}
        quickPrompts={getQuickPromptsForActivityType(activity.type)}
        onContentGenerated={handleAiGeneratedContent}
      />

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete activity</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{activity.title}"?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivityEditor; 